"use client";

import React, { useEffect, useRef, useState } from "react";

interface ScrollRevealProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  /**
   * Render visible in the server HTML, with no observer and no fade.
   *
   * For a section that is already in the viewport on load, the reveal is an
   * animation nobody sees the beginning of — and the cost is the page's Largest
   * Contentful Paint. Chrome does not consider an `opacity: 0` element as an LCP
   * candidate, so while the default state hides the section, LCP cannot fire on
   * anything inside it: not until the bundle downloads, React hydrates, the
   * IntersectionObserver fires, `delay` elapses and the 1s transition starts.
   * On the pages that matter that section holds the <h1> and the hero image, so
   * the whole measurement waits on hydration and the hero's `priority` preload
   * is spent on an image that cannot paint yet.
   *
   * Set by BlockRenderer for the first visible block. Everything below the fold
   * keeps the reveal, which is the only place it was ever doing anything.
   */
  immediate?: boolean;
}

export function ScrollReveal({
  children,
  className = "",
  delay = 200,
  immediate = false,
}: ScrollRevealProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(immediate);

  useEffect(() => {
    if (immediate) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setIsVisible(true);
      return;
    }

    const el = sectionRef.current;
    if (!el) return;

    let timer: NodeJS.Timeout;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          timer = setTimeout(() => {
            setIsVisible(true);
          }, delay);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -30px 0px" }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, [delay, immediate]);

  return (
    <div
      ref={sectionRef}
      className={`transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] ${
        isVisible
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-8 pointer-events-none"
      } ${className}`}
    >
      {children}
    </div>
  );
}
